import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ContactEditDialog } from "@/app/Contacts/Components/ContactEditDialog";
import {
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ContactRecord } from "@/app/Contacts/GraphQL/contactRecordQuery";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { generateInvoicesForContactMutation } from "@/app/Contacts/GraphQL/contactGenerateInvoicesMutation";
import { deleteBillingEntityMutation } from "@/app/Settings/Settings/GraphQL/deleteBillingEntityMutation";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { ContactType } from "@/gql/graphql";

export type ContactActionTarget = Pick<
  ContactRecord,
  "id" | "type" | "name" | "legal_name"
>;

export function useContactActions(): ActionsHook<ContactActionTarget> {
  const { open } = useDialog(ContactEditDialog);
  const navigate = useNavigate();
  const { mutateAsync } = useGqlMutation(generateInvoicesForContactMutation);
  const { mutateAsync: deleteBillingEntity } = useGqlMutation(
    deleteBillingEntityMutation,
  );
  const { open: openConfirmDelete } = useConfirmDeleteDialog();

  async function handleGenerateInvoices(contactId: string) {
    const result = await mutateAsync({
      contactId,
      asDraft: true,
    });

    const invoices = result.generateInvoicesForContact;

    if (invoices.length === 0) {
      toast.info("No invoices to generate");
      return;
    }

    toast.success(
      `Generated ${invoices.length} draft invoice${invoices.length > 1 ? "s" : ""}`,
    );

    if (invoices.length === 1) {
      navigate({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id",
        params: (prev) => ({ ...prev, id: invoices[0].id }),
      });
    }
  }

  return (model) => {
    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Edit",
                Icon: PencilIcon,
                handleAction: () => {
                  open({
                    contactId: model.id,
                  });
                },
              },
              {
                label: "Generate invoices",
                Icon: DocumentTextIcon,
                isAdminAction: true,
                handleAction: () => handleGenerateInvoices(model.id),
              },
              {
                label: "Delete billing entity",
                Icon: TrashIcon,
                color: "red",
                isHidden:
                  model.type !== ContactType.Entity &&
                  model.type !== ContactType.Guest,
                handleAction: () => {
                  openConfirmDelete({
                    title: "Delete billing entity",
                    message: `Are you sure you want to delete the billing entity ${
                      model.name ?? model.legal_name ?? ""
                    }? This cannot be undone.`,
                    buttonText: "Delete",
                    onDelete: async () => {
                      await deleteBillingEntity({ id: model.id });
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
